import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RefreshCw, ChevronRight } from "lucide-react";
import ZoneSettingsEditLanePopup from "./zone-storage-settings-edit-lane";
import { logoutProcessor } from "./helpers/logoutProcessor";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";

type laneInfo = {
    Warehouse: string,
    Date: string,
    Batch: number,
    Lane: string,
    AdditionalLanes?: string[],
    Type: string,
    Ready: boolean,
    InUse: boolean,
    InUseUser: string,
    AssignedBay: string,
    Complete: boolean,
    TotalItems: number,
    TotalScannedItems: number
};

const ZoneStorageSettingsLanes = ({ warehouse, selectedDate, changeDateFunc }: { warehouse: string, selectedDate: string, changeDateFunc: (date: string, sorter: number) => void }) => {
    const navigator = useNavigate();
    const { t } = useTranslation();

    const [allLanesData, set_allLaneData] = useState<laneInfo[]>([]);
    const [editLane, set_editLane] = useState<laneInfo>({
        Warehouse: "", Batch: 0, Date: "", Lane: "", Type: "", Ready: false,
        InUse: false, InUseUser: "", AssignedBay: "", Complete: false,
        TotalItems: 0, TotalScannedItems: 0
    });
    const [oldestDate, set_oldestDate] = useState<string>("");
    const [oldestBatch, set_oldestBatch] = useState<number>(0);
    const [editLanePopup, set_editLanePopup] = useState<boolean>(false);
    const [reloading, set_reloading] = useState<boolean>(false);

    const fetchLanes = async () => {
        await apiFetch(`/zonestorage/lanes/get-lanes-for-settings-sorter-two`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ warehouse })
        }).then(async (res): Promise<any> => {
            const response = await res.json();
            if (res.ok) {
                if (response.length === 0) {
                    set_allLaneData([]);
                    set_oldestDate("");
                    set_oldestBatch(0);
                    return;
                }
                let sortedLanes: laneInfo[] = [];
                if (warehouse === "Newport") {
                    sortedLanes = response.sort((a: laneInfo, b: laneInfo) =>
                        (new Date(a.Date).getTime() - new Date(b.Date).getTime()) || (a.Batch - b.Batch) || (a.Type.localeCompare(b.Type)) || (a.Lane.localeCompare(b.Lane)));
                } else {
                    sortedLanes = response.sort((a: laneInfo, b: laneInfo) =>
                        (new Date(a.Date).getTime() - new Date(b.Date).getTime()) || (a.Batch - b.Batch) || (a.Lane.padStart(2, '0').localeCompare(b.Lane.padStart(2, '0'))));
                }

                set_allLaneData(sortedLanes);
                set_oldestDate(sortedLanes[0].Date);
                set_oldestBatch(sortedLanes[0].Batch);
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                toast.error(response.message);
            }
        });
    };

    useEffect(() => { fetchLanes(); }, [warehouse, selectedDate]);

    const update_lanes = async () => {
        set_reloading(true);
        await fetchLanes();
        set_reloading(false);
    };

    const getStatus = (lane: laneInfo): { text: string; cls: string } => {
        if (lane.Complete) return { text: t("lane_complete"), cls: "lane-status-complete" };
        if (lane.AssignedBay) return { text: `Bay ${lane.AssignedBay.slice(-2)}`, cls: "lane-status-bay" };
        if (lane.Ready) return { text: t("lane_ready"), cls: "lane-status-ready" };
        return { text: t("lane_not_ready"), cls: "lane-status-not-ready" };
    };

    const getRowClass = (lane: laneInfo): string => {
        if (lane.Complete) return "lane-row-complete";
        if (lane.Date === oldestDate && lane.Batch === oldestBatch) return "lane-row-oldest";
        return "lane-row-newer";
    };

    const getPct = (lane: laneInfo): number =>
        lane.TotalItems > 0 ? Math.round((lane.TotalScannedItems / lane.TotalItems) * 1000) / 10 : 100;

    return (
        <div className="zs-card">
            <div className="flex-apart" style={{ marginBottom: 8 }}>
                <h2 className="zs-card-title" style={{ margin: 0 }}>{t("sorter_lanes")}</h2>
                <RefreshCw className={"refresh-button " + (reloading ? "reload-spin" : "")} onClick={update_lanes} />
            </div>

            <div className="lane-table">
                {/* Header */}
                <div className="lane-header">
                    <span className="lane-col-num">#</span>
                    <span className="lane-col-batch">Batch</span>
                    <span className="lane-col-date">Date</span>
                    <span className="lane-col-type">Type</span>
                    <span className="lane-col-progress">Progress</span>
                    <span className="lane-col-status">Status</span>
                    <span className="lane-col-action"></span>
                </div>

                {/* Rows */}
                {allLanesData.map((lane) => {
                    const pct = getPct(lane);
                    const status = getStatus(lane);
                    return (
                        <div
                            className={`lane-row ${getRowClass(lane)}`}
                            key={lane.Lane + lane.Date + lane.Batch}
                            onClick={() => { set_editLane(lane); set_editLanePopup(true); }}
                        >
                            <span className="lane-col-num lane-num">
                                {[lane.Lane, ...(lane.AdditionalLanes ?? [])].map((n, i) => (
                                    <span key={i}>
                                        {i > 0 && <span className="zone-select-lane-sep">&nbsp;&amp;&nbsp;</span>}
                                        {n}
                                    </span>
                                ))}
                            </span>
                            <span className="lane-col-batch">{lane.Batch}</span>
                            <span className="lane-col-date">{new Date(lane.Date).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })}</span>
                            <span className="lane-col-type">{lane.Type}</span>
                            <span className="lane-col-progress">
                                <span className="lane-progress-bar">
                                    <span className="lane-progress-fill" style={{ width: `${pct}%` }} />
                                </span>
                                <span className="lane-progress-pct">{pct}%</span>
                            </span>
                            <span className={`lane-col-status ${status.cls}`}>{status.text}</span>
                            <span className="lane-col-action"><ChevronRight size={16} /></span>
                        </div>
                    );
                })}
            </div>

            {editLanePopup && <ZoneSettingsEditLanePopup warehouse={warehouse} laneToEdit={editLane} additionalLanes={editLane.AdditionalLanes} set_editLanePopup={set_editLanePopup} laneUpdater={update_lanes} />}
        </div>
    );
};

export default ZoneStorageSettingsLanes;
